import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import api from '../../../services/api';
import { getSubdomainUsername, getUsernameSyncOnly } from '../../../utils/subdomain';

const PublicPricing = () => {
  const { username: urlUsername } = useParams();
  const subdomainUser = getSubdomainUsername();
  const cachedCustomDomainUser = getUsernameSyncOnly();
  const username = urlUsername || subdomainUser || cachedCustomDomainUser;

  const [data, setData] = useState({ section: null, plans: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/business/pricing/public/${username}/`);
        // API returns array directly
        const plans = Array.isArray(res.data) ? res.data : (res.data.plans || res.data.items || []);
        setData({
          section: res.data.section || {},
          plans: plans
        });
      } catch (err) {
        console.error('Error fetching pricing:', err);
      } finally {
        setLoading(false);
      }
    };
    if (username) fetchData();
  }, [username]);

  if (loading) {
    return (
      <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <div style={{
          width: '2rem', height: '2rem',
          border: '3px solid rgba(255,255,255,0.1)',
          borderTopColor: '#6366f1',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto'
        }} />
      </div>
    );
  }

  const bgColor = data.section?.background_color || '#1e293b';

  if (!data.plans.length) {
    return (
      <div id="section-pricing" style={{ padding: '4rem 2rem', textAlign: 'center', backgroundColor: bgColor }}>
        <p style={{ color: 'rgba(255,255,255,0.5)' }}>No pricing plans available yet.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '4rem 2rem', backgroundColor: bgColor }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', color: '#fff', marginBottom: '0.5rem' }}>
            {data.section?.title || 'Pricing'}
          </h2>
          {data.section?.subtitle && (
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem' }}>
              {data.section.subtitle}
            </p>
          )}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '2rem',
          alignItems: 'stretch'
        }}>
          {data.plans.map((plan, index) => (
            <div
              key={plan.id || index}
              style={{
                padding: '2rem',
                backgroundColor: plan.is_popular ? 'rgba(99, 102, 241, 0.1)' : 'rgba(255,255,255,0.05)',
                borderRadius: '1rem',
                border: plan.is_popular ? '2px solid #6366f1' : '1px solid rgba(255,255,255,0.1)',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              {plan.is_popular && (
                <div style={{
                  position: 'absolute',
                  top: '-0.75rem',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  padding: '0.25rem 1rem',
                  backgroundColor: '#6366f1',
                  color: '#fff',
                  borderRadius: '999px',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  Most Popular
                </div>
              )}

              <h3 style={{ color: '#fff', fontSize: '1.5rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                {plan.name}
              </h3>

              {plan.description && (
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  {plan.description}
                </p>
              )}

              <div style={{ marginBottom: '1.5rem' }}>
                <span style={{ color: '#fff', fontSize: '3rem', fontWeight: '700' }}>
                  {plan.currency || '$'}{plan.price}
                </span>
                {plan.period && (
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1rem' }}>
                    /{plan.period}
                  </span>
                )}
              </div>

              {/* Features list */}
              {plan.features && (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, flex: 1, marginBottom: '1.5rem' }}>
                  {(Array.isArray(plan.features) ? plan.features : plan.features.split('\n')).map((feature, i) => {
                    const isIncluded = !feature.startsWith('-');
                    const featureText = feature.replace(/^[-+]\s*/, '');
                    return (
                      <li key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.5rem',
                        color: isIncluded ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.4)'
                      }}>
                        {isIncluded ? (
                          <Check size={16} style={{ color: '#10b981', flexShrink: 0 }} />
                        ) : (
                          <X size={16} style={{ color: '#ef4444', flexShrink: 0 }} />
                        )}
                        <span style={{ textDecoration: isIncluded ? 'none' : 'line-through' }}>
                          {featureText}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}

              {plan.cta_text && (
                <a
                  href={plan.cta_link || '#contact'}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '0.75rem 1.5rem',
                    backgroundColor: plan.is_popular ? '#6366f1' : 'transparent',
                    border: plan.is_popular ? 'none' : '1px solid #6366f1',
                    color: plan.is_popular ? '#fff' : '#6366f1',
                    borderRadius: '0.5rem',
                    textAlign: 'center',
                    fontWeight: '600',
                    textDecoration: 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  {plan.cta_text}
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PublicPricing;
